import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Eye, History, Loader2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');

const CompaniesList = ({ versionId }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    axios.get(`${API}/settings/aydinlatma-versions/${versionId}/companies`).then(res => setItems(res.data)).catch(() => setItems([]));
  }, [versionId]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="version-companies-empty">Bu sürümü onaylayan firma yok.</p>;
  return (
    <ul className="divide-y max-h-[60vh] overflow-y-auto" data-testid="version-companies-list">
      {items.map(c => (
        <li key={c.id} className="py-2 flex items-center justify-between gap-3 text-sm">
          <span className="min-w-0"><span className="font-medium text-slate-800">{c.name}</span>{c.trade_name && <span className="block text-xs text-slate-500 truncate">{c.trade_name}</span>}</span>
          <span className="text-xs text-slate-500 text-right shrink-0">{fmt(c.consent?.accepted_at)}{c.consent?.accepted_by && <span className="block">{c.consent.accepted_by}</span>}</span>
        </li>
      ))}
    </ul>
  );
};

const VersionRow = ({ v, onText, onCompanies, onRestore, restoring }) => (
  <li className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-center gap-2 ${v.current ? 'border-emerald-200 bg-emerald-50/40' : ''}`} data-testid={`aydinlatma-version-${v.version}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-800">v{v.version}</span>
        {v.current && <Badge className="bg-emerald-100 text-emerald-700">Güncel</Badge>}
      </div>
      <p className="text-xs text-slate-500">{fmt(v.created_at)} · {v.created_by}</p>
    </div>
    <div className="flex items-center gap-1 flex-wrap">
      <Button size="sm" variant="ghost" onClick={onText} data-testid={`aydinlatma-version-text-${v.version}`}><Eye className="h-4 w-4 mr-1" />Metni Gör</Button>
      <Button size="sm" variant="ghost" onClick={onCompanies} data-testid={`aydinlatma-version-companies-${v.version}`}><Building2 className="h-4 w-4 mr-1" />Firmalar ({v.accepted_count})</Button>
      {!v.current && (
        <Button size="sm" variant="outline" onClick={onRestore} disabled={restoring} data-testid={`aydinlatma-version-restore-${v.version}`}>
          {restoring ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}Geri Yükle
        </Button>
      )}
    </div>
  </li>
);

export const AydinlatmaVersionsCard = ({ refreshKey, onRestored }) => {
  const [versions, setVersions] = useState(null);
  const [view, setView] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/settings/aydinlatma-versions`).then(res => setVersions(res.data)).catch(() => setVersions([]));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const restore = async (v) => {
    setRestoring(v.id);
    try {
      const res = await axios.post(`${API}/settings/aydinlatma-versions/${v.id}/restore`);
      toast.success(`v${res.data.restored_from} geri yüklendi, yeni sürüm: v${res.data.version}. Firmalardan yeniden onay istenecek.`);
      load();
      onRestored?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Geri yüklenemedi');
    } finally { setRestoring(null); }
  };

  return (
    <Card className="border-slate-200" data-testid="aydinlatma-versions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" />Aydınlatma Metni Sürüm Geçmişi</CardTitle>
        <CardDescription>Her değişiklik yeni bir sürüm olarak saklanır. Güncel sürümü onaylamamış firmalardan girişte yeniden onay istenir.</CardDescription>
      </CardHeader>
      <CardContent>
        {!versions ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <ul className="space-y-2">
            {versions.map(v => (
              <VersionRow key={v.id} v={v} restoring={restoring === v.id}
                onText={() => setView({ type: 'text', v })} onCompanies={() => setView({ type: 'companies', v })} onRestore={() => restore(v)} />
            ))}
          </ul>
        )}
      </CardContent>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl" data-testid="aydinlatma-version-dialog">
          <DialogHeader><DialogTitle>v{view?.v.version} · {view?.type === 'text' ? 'Metin' : 'Onaylayan Firmalar'}</DialogTitle></DialogHeader>
          {view?.type === 'text' && <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm text-slate-700" data-testid="aydinlatma-version-text">{view.v.text}</div>}
          {view?.type === 'companies' && <CompaniesList versionId={view.v.id} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
